import React, { useContext, useEffect, useState } from "react";
import { IIngredient } from "../interfaces";
import {
  Button,
  Dialog,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { UserContext } from "../contexts/userContext";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../firebase-config";
import {
  BUSINESS_COLLECTION,
  INGREDIENTS_COLLECTION,
} from "../utils/constants";
import { LoadingButton } from "@mui/lab";

const CreateIngredientModal = ({
  show,
  ingredient,
  handleClose,
  updateItem,
  getList,
}: {
  show: boolean;
  ingredient: IIngredient;
  handleClose(): void;
  updateItem(item: IIngredient): void;
  getList(): void;
}) => {
  const { businessData } = useContext(UserContext);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState({} as IIngredient);
  const subCollectionRef = collection(
    db,
    BUSINESS_COLLECTION,
    businessData.uid,
    INGREDIENTS_COLLECTION
  );

  const handleChange = (name: string, value: any) =>
    setItem({ ...item, [name]: value });

  const handleSubmit = async () => {
    setLoading(true);
    const totalItem = {
      ...item,
      stock_quantity: item.stock_quantity || 0,
      min_quantity: item.min_quantity || 1,
      status: "enabled",
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await addDoc(subCollectionRef, totalItem)
      .then(() => {
        getList();
        handleClose();
      })
      .finally(() => setLoading(false));
  };

  const handleUpdate = () => {
    try {
      setLoading(true);
      updateItem({ ...item, updatedAt: new Date() });
    } catch (error) {
      console.log("ERROR", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(ingredient).length) {
      setIsEdit(true);
      setItem(ingredient);
    }
    return () => {
      setIsEdit(false);
      setItem({} as IIngredient);
    };
  }, [ingredient]);
  return (
    <Dialog open={show} onClose={handleClose} fullWidth>
      <IconButton onClick={handleClose} className="close-icon">
        <span className="material-symbols-rounded">close</span>
      </IconButton>
      <DialogTitle>Nuevo ingrediente</DialogTitle>
      <Grid container sx={{ p: 3 }} spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            name="ingredient-code"
            label="Codigo (opcional)"
            placeholder="Codigo"
            size="small"
            value={item.code || ""}
            onChange={({ target: { value } }) => handleChange("code", value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            required
            fullWidth
            name="ingredient-name"
            label="Nombre"
            placeholder="Nombre del producto"
            size="small"
            value={item.name || ""}
            onChange={({ target: { value } }) => handleChange("name", value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            required
            fullWidth
            name="stock_quantity"
            label="Cantidad"
            placeholder="Ingrese la cantidad"
            size="small"
            type="number"
            value={item.stock_quantity || ""}
            onChange={({ target: { name, value } }) =>
              handleChange(name, Number(value))
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            required
            fullWidth
            name="min_quantity"
            label="Cantidad mínima"
            placeholder="Ingrese la cantidad"
            size="small"
            type="number"
            value={item.min_quantity || ""}
            onChange={({ target: { name, value } }) =>
              handleChange(name, Number(value))
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="unit-select">Unidad de contabilidad</InputLabel>
            <Select
              labelId="unit-select"
              id="demo-select"
              name="unit"
              value={item.unit || ""}
              label="Unidad de contabilidad"
              onChange={({ target: { name, value } }) =>
                handleChange(name, value)
              }
            >
              <MenuItem value="kg">Kilogramos</MenuItem>
              <MenuItem value="lt">Litros</MenuItem>
              <MenuItem value="piece">Unidad</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="unit-select">Unidad de consumo</InputLabel>
            <Select
              labelId="unit-select"
              id="demo-select"
              name="consumption_unit"
              value={item.consumption_unit || ""}
              label="Unidad de consumo"
              onChange={({ target: { name, value } }) =>
                handleChange(name, value)
              }
            >
              <MenuItem value="ml">Mililitros</MenuItem>
              <MenuItem value="gr">Gramos</MenuItem>
              <MenuItem value="piece">Unidad</MenuItem>
              <MenuItem value="kg">Kilogramos</MenuItem>
              <MenuItem value="lt">Litros</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid
          container
          display="flex"
          justifyContent="end"
          alignItems="center"
          size={{ xs: 12 }}
          sx={{ mt: 3 }}
          spacing={1}
        >
          <Button
            color="secondary"
            variant="outlined"
            size="small"
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <LoadingButton
            loading={loading}
            variant="contained"
            color="secondary"
            disabled={loading || !Object.keys(item).length}
            size="small"
            onClick={() => (isEdit ? handleUpdate() : handleSubmit())}
          >
            {isEdit ? "Editar" : "Crear"}
          </LoadingButton>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default CreateIngredientModal;
